import { apiUrl } from '../../config/api';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, Building2, ChevronLeft, ChevronRight, MapPin, Sparkles, Star, TrendingDown,
} from 'lucide-react';
import { UserAuthContext } from '../../UserAuthContext';
import { FavoritesContext } from '../../FavoritesContext';
import FavoriteButton from '../FavoriteButton';
import { getRecentlyViewedProductIds } from '../../utils/recentlyViewed';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { useLanguage, useLocalizedPath } from '../../contexts/LanguageContext';
import { productRoute } from '../../utils/namedRoutes';
import { useCurrency } from '../../contexts/CurrencyContext';
import {
  appendLocationPreference,
  fetchHomepageJson,
  homepageAuthHeaders,
  homepageViewerScope,
  HOMEPAGE_CACHE_TTL,
} from '../../utils/homepageApi';
import { useHomeSections } from './HomeSectionsContext';
import './ProductShowcase.css';

const MAX_PRODUCTS_PER_TAB = 24;

// One tabbed showcase replaces the separate Recommended, Best Sellers, Hot
// Deals, Just Added and Latest Auctions carousels. The anchors keep the old
// #hot-deals / #best-sellers / #just-added / #latest-auctions links working.
const TABS = [
  { key: 'recommended', label: 'Recommended', anchor: 'recommended-products' },
  { key: 'deals', label: 'Hot Deals', anchor: 'hot-deals', subtitle: 'Limited time offers with amazing discounts' },
  { key: 'bestSellers', label: 'Best Sellers', anchor: 'best-sellers', subtitle: 'The most purchased products in the last 7 days' },
  { key: 'recent', label: 'Just Added', anchor: 'just-added', subtitle: "Fresh arrivals you don't want to miss" },
  { key: 'auctions', label: 'Auctions', anchor: 'latest-auctions', subtitle: 'Bid before these auctions close' },
];

const TAB_BY_ANCHOR = Object.fromEntries(TABS.map((tab) => [`#${tab.anchor}`, tab.key]));

const RECOMMENDATION_SOURCES = {
  favorite: 'favorites',
  browsing_history: 'browsing history',
  session_browsing: 'recent browsing',
  past_order: 'past orders',
};

const emptyTabState = () => ({ products: [], loading: true, error: false });

const formatCountdown = (endValue, now) => {
  const remaining = new Date(endValue).getTime() - now;
  if (!Number.isFinite(remaining) || remaining <= 0) return 'Ended';
  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const minutes = Math.floor((remaining % 3600000) / 60000);
  return `${days}d ${hours}h ${minutes}m`;
};

const hasPrice = (product) => (
  product.priceAvailable !== false
  && product.price_available !== false
  && product.price != null
);

const prefersReducedMotion = () => Boolean(
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
);

function ShowcaseCard({ product, kind, now }) {
  const { formatMoney } = useCurrency();
  const { t } = useLanguage();
  const localizedPath = useLocalizedPath();
  const isAuction = kind === 'auctions';
  const discount = Number(product.discount) > 0 ? Number(product.discount) : null;
  const savings = discount && product.originalPrice != null
    ? Math.max(0, Number(product.originalPrice) - Number(product.price))
    : 0;
  const rating = product.rating != null && Number.isFinite(Number(product.rating)) ? Number(product.rating) : null;
  const location = product.seller?.location;
  const sellerName = product.seller?.name;

  let price;
  if (isAuction) {
    price = (
      <>
        <span className="showcase-card__price-label">
          {t(product.latestBid ? 'Latest bid for the full lot' : 'Minimum bid for the full lot')}
        </span>
        <strong className="showcase-card__price">
          {formatMoney(product.latestBid || product.minimumBid || product.price, product.priceCurrency)}
        </strong>
      </>
    );
  } else if (!hasPrice(product)) {
    price = <strong className="showcase-card__price showcase-card__price--quote">Price on request</strong>;
  } else {
    price = (
      <>
        <span className="showcase-card__price-row">
          <strong className="showcase-card__price">{formatMoney(product.price, product.priceCurrency)}</strong>
          {product.originalPrice != null && Number(product.originalPrice) > Number(product.price) && (
            <s className="showcase-card__original">{formatMoney(product.originalPrice, product.priceCurrency)}</s>
          )}
        </span>
        {savings > 0 && (
          <span className="showcase-card__savings">
            You save {formatMoney(savings.toFixed(2), product.priceCurrency)}
          </span>
        )}
        {kind === 'deals' && product.minimumQuantity > 1 && (
          <span className="showcase-card__note">{`Deal applies from ${product.minimumQuantity} units`}</span>
        )}
      </>
    );
  }

  return (
    <article className="showcase-card">
      <div className="showcase-card__media">
        {product.image && <img src={product.image} alt="" loading="lazy" decoding="async" />}
        <div className="showcase-card__badges">
          {product.sponsored && (
            <span className="showcase-card__badge showcase-card__badge--sponsored">
              <Sparkles size={12} aria-hidden="true" /> Sponsored · Paid advertising
            </span>
          )}
          {discount && !isAuction && (
            <span className="showcase-card__badge showcase-card__badge--discount">
              <TrendingDown size={13} aria-hidden="true" /> -{discount}%
            </span>
          )}
        </div>
        {(isAuction || product.condition) && (
          <span className="showcase-card__condition">{isAuction ? 'Auction' : product.condition}</span>
        )}
        <FavoriteButton productId={product.id} />
      </div>

      <div className="showcase-card__body">
        {product.brand && <div className="showcase-card__brand">{product.brand}</div>}
        <h3 className="showcase-card__name">
          {/* The ::after of this link stretches over the whole card. */}
          <Link to={localizedPath(productRoute(product))}>{product.name}</Link>
        </h3>

        {rating != null && (
          <div className="showcase-card__rating">
            <Star size={13} fill="currentColor" aria-hidden="true" />
            {rating.toFixed(1)}
            {product.reviews != null && <span>({Number(product.reviews).toLocaleString()})</span>}
          </div>
        )}

        {kind === 'recommended' && product.matchReasons?.length > 0 && (
          <div className="showcase-card__reasons" aria-label="Why this product is recommended">
            {product.matchReasons.map((reason) => <span key={reason}>{reason}</span>)}
          </div>
        )}

        <div className="showcase-card__pricing">{price}</div>

        {isAuction && (
          <div className="showcase-card__countdown">
            <span>{t('Closes in')}</span>
            <strong>{t(formatCountdown(product.auctionEndsAt, now))}</strong>
          </div>
        )}

        {(sellerName || location) && (
          <div className="showcase-card__meta">
            {sellerName && <span><Building2 size={14} aria-hidden="true" /> {sellerName}</span>}
            {location && <span><MapPin size={14} aria-hidden="true" /> {location}</span>}
          </div>
        )}

        <span className="showcase-card__cta" aria-hidden="true">
          {isAuction ? 'View Details' : 'View Product'} <ArrowRight size={16} />
        </span>
      </div>
    </article>
  );
}

function SkeletonCards() {
  return Array.from({ length: 4 }, (_, index) => (
    <div className="showcase-card showcase-card--skeleton" key={index} aria-hidden="true">
      <div className="showcase-card__media" />
      <div className="showcase-card__body">
        <span className="showcase-skeleton-line showcase-skeleton-line--short" />
        <span className="showcase-skeleton-line" />
        <span className="showcase-skeleton-line showcase-skeleton-line--price" />
      </div>
    </div>
  ));
}

export default function ProductShowcase() {
  const { user, loading: authLoading, location, locationReady } = useContext(UserAuthContext);
  const { personalizationAllowed } = useCookieConsent();
  const { favorites } = useContext(FavoritesContext);
  const { setAvailability, tabRequest } = useHomeSections();
  const localizedPath = useLocalizedPath();
  const navigate = useNavigate();

  const [tabs, setTabs] = useState(() => Object.fromEntries(TABS.map((tab) => [tab.key, emptyTabState()])));
  const [recommendationInfo, setRecommendationInfo] = useState({ personalized: false, basedOn: [] });
  const [selectedTab, setSelectedTab] = useState('recommended');
  const [recentlyViewed, setRecentlyViewed] = useState(getRecentlyViewedProductIds);
  const [now, setNow] = useState(Date.now());
  const [scrollState, setScrollState] = useState({ canPrev: false, canNext: false });
  const sectionRef = useRef(null);
  const rowRef = useRef(null);
  const tabRefs = useRef({});

  const favoriteSignature = useMemo(
    () => favorites.map((favorite) => favorite.productid || favorite.id).sort().join(','),
    [favorites]
  );
  const recentSignature = recentlyViewed.join(',');

  const setTab = useCallback((key, patch) => {
    setTabs((previous) => ({ ...previous, [key]: { ...previous[key], ...patch } }));
  }, []);

  useEffect(() => {
    const refreshSignals = (event) => {
      setRecentlyViewed(Array.isArray(event.detail) ? event.detail : getRecentlyViewedProductIds());
    };
    window.addEventListener('recommendationSignalsUpdated', refreshSignals);
    return () => window.removeEventListener('recommendationSignalsUpdated', refreshSignals);
  }, []);

  // Recommendations depend on favorites and browsing history, so they reload
  // independently from the other tabs.
  useEffect(() => {
    if (authLoading || !locationReady) return undefined;
    const controller = new AbortController();
    setTab('recommended', emptyTabState());

    const endpoint = new URL(apiUrl('/api/recommended-products'));
    if (recentSignature) endpoint.searchParams.set('recent', recentSignature);
    endpoint.searchParams.set('personalization', personalizationAllowed ? '1' : '0');
    appendLocationPreference(endpoint, location);

    fetchHomepageJson(endpoint.toString(), {
      headers: homepageAuthHeaders(user),
      scope: homepageViewerScope(user),
      variant: favoriteSignature,
      signal: controller.signal,
    })
      .then((result) => {
        setTab('recommended', {
          products: Array.isArray(result?.products) ? result.products : [],
          loading: false,
          error: false,
        });
        setRecommendationInfo({
          personalized: Boolean(result?.personalized),
          basedOn: Array.isArray(result?.basedOn) ? result.basedOn : [],
        });
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('Error loading recommended products:', error);
        setTab('recommended', { products: [], loading: false, error: true });
      });

    return () => controller.abort();
  }, [authLoading, favoriteSignature, recentSignature, user, location, locationReady, personalizationAllowed, setTab]);

  useEffect(() => {
    if (!locationReady) return undefined;
    const controller = new AbortController();
    const recent = personalizationAllowed ? getRecentlyViewedProductIds().join(',') : '';

    const sources = [
      { key: 'deals', path: '/api/hot-deals' },
      { key: 'bestSellers', path: '/api/best-sellers' },
      { key: 'recent', path: '/api/recent-products', ttlMs: HOMEPAGE_CACHE_TTL.fast },
      { key: 'auctions', path: '/api/recent-products', params: { listing_type: 'auction' }, ttlMs: HOMEPAGE_CACHE_TTL.live },
    ];

    sources.forEach(({ key, path, params = {}, ttlMs }) => {
      setTab(key, emptyTabState());
      const endpoint = new URL(apiUrl(path));
      endpoint.searchParams.set('personalization', personalizationAllowed ? '1' : '0');
      Object.entries(params).forEach(([name, value]) => endpoint.searchParams.set(name, value));
      appendLocationPreference(endpoint, location);
      if (recent) endpoint.searchParams.set('recent', recent);

      fetchHomepageJson(endpoint.toString(), {
        headers: homepageAuthHeaders(user),
        scope: homepageViewerScope(user),
        signal: controller.signal,
        ...(ttlMs ? { ttlMs } : {}),
      })
        .then((data) => setTab(key, { products: Array.isArray(data) ? data : [], loading: false, error: false }))
        .catch((error) => {
          if (error.name === 'AbortError') return;
          console.error(`Error loading homepage products (${key}):`, error);
          setTab(key, { products: [], loading: false, error: true });
        });
    });

    return () => controller.abort();
  }, [user, location, locationReady, personalizationAllowed, setTab]);

  // A tab is shown while it loads and hidden once it is known to be empty.
  const visibleTabs = TABS.filter((tab) => tabs[tab.key].loading || tabs[tab.key].products.length > 0);
  const allLoaded = TABS.every((tab) => !tabs[tab.key].loading);
  const activeKey = visibleTabs.some((tab) => tab.key === selectedTab)
    ? selectedTab
    : visibleTabs[0]?.key;
  const activeTab = TABS.find((tab) => tab.key === activeKey);
  const activeState = activeKey ? tabs[activeKey] : null;
  const activeProducts = activeState ? activeState.products.slice(0, MAX_PRODUCTS_PER_TAB) : [];

  useEffect(() => {
    TABS.forEach((tab) => {
      const state = tabs[tab.key];
      setAvailability(tab.key, !state.loading && state.products.length > 0);
    });
  }, [tabs, setAvailability]);

  // Auction countdowns only need to tick while that tab is on screen.
  useEffect(() => {
    if (activeKey !== 'auctions') return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [activeKey]);

  const scrollToSection = useCallback(() => {
    sectionRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }, []);

  // Quick access links in the hero ask for a specific tab.
  useEffect(() => {
    if (!tabRequest) return;
    setSelectedTab(tabRequest.tab);
    scrollToSection();
  }, [tabRequest, scrollToSection]);

  // Old deep links such as /#hot-deals open the matching tab.
  useEffect(() => {
    const openFromHash = () => {
      const key = TAB_BY_ANCHOR[window.location.hash];
      if (!key) return;
      setSelectedTab(key);
      window.requestAnimationFrame(scrollToSection);
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
  }, [scrollToSection]);

  const updateScrollState = useCallback(() => {
    const row = rowRef.current;
    if (!row) {
      setScrollState({ canPrev: false, canNext: false });
      return;
    }
    const maxScroll = row.scrollWidth - row.clientWidth;
    setScrollState({ canPrev: row.scrollLeft > 4, canNext: row.scrollLeft < maxScroll - 4 });
  }, []);

  useEffect(() => {
    if (rowRef.current) rowRef.current.scrollLeft = 0;
    updateScrollState();
  }, [activeKey, activeProducts.length, updateScrollState]);

  useEffect(() => {
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [updateScrollState]);

  const scrollRow = (direction) => {
    const row = rowRef.current;
    if (!row) return;
    row.scrollBy({ left: direction * row.clientWidth * 0.9, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  const handleTabKeyDown = (event, index) => {
    const moves = { ArrowRight: 1, ArrowLeft: -1 };
    let nextIndex;
    if (event.key in moves) nextIndex = (index + moves[event.key] + visibleTabs.length) % visibleTabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = visibleTabs.length - 1;
    if (nextIndex === undefined) return;
    event.preventDefault();
    const nextKey = visibleTabs[nextIndex].key;
    setSelectedTab(nextKey);
    tabRefs.current[nextKey]?.focus();
  };

  // Nothing to show at all: keep the page short instead of an empty block.
  if (allLoaded && visibleTabs.length === 0) return null;

  let subtitle = activeTab?.subtitle;
  if (activeKey === 'recommended') {
    const sources = recommendationInfo.basedOn
      .map((source) => RECOMMENDATION_SOURCES[source])
      .filter(Boolean)
      .join(', ');
    subtitle = recommendationInfo.personalized
      ? `Selected from your ${sources || 'marketplace activity'} and matched by sector, brand, category and company.`
      : user
        ? 'Start exploring products; recommendations will adapt as your browsing history grows.'
        : 'Sign in to receive suggestions based on your favorites, browsing history and past orders.';
  }

  return (
    <section
      className="product-showcase"
      id="product-showcase"
      ref={sectionRef}
      aria-labelledby="product-showcase-title"
    >
      {/* Invisible targets so in-page links to the former sections still resolve. */}
      {TABS.map((tab) => <span key={tab.anchor} id={tab.anchor} className="product-showcase__anchor" />)}

      <div className="product-showcase__container">
        <header className="product-showcase__header">
          <div>
            <h2 id="product-showcase-title">Explore Products</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {activeKey === 'recommended' && !user && (
            <button
              type="button"
              className="product-showcase__signin"
              onClick={() => navigate(localizedPath('/login'))}
            >
              Sign In <ArrowRight size={16} />
            </button>
          )}
        </header>

        <div className="product-showcase__tabs" role="tablist" aria-label="Product lists">
          {visibleTabs.map((tab, index) => {
            const selected = tab.key === activeKey;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                id={`product-showcase-tab-${tab.key}`}
                aria-selected={selected}
                aria-controls="product-showcase-panel"
                tabIndex={selected ? 0 : -1}
                className={`product-showcase__tab${selected ? ' is-active' : ''}`}
                ref={(node) => { tabRefs.current[tab.key] = node; }}
                onClick={() => setSelectedTab(tab.key)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div
          className="product-showcase__panel"
          id="product-showcase-panel"
          role="tabpanel"
          aria-labelledby={activeKey ? `product-showcase-tab-${activeKey}` : undefined}
          aria-busy={activeState?.loading || undefined}
        >
          {activeState?.loading || !activeKey ? (
            <div className="product-showcase__row product-showcase__row--loading">
              <SkeletonCards />
            </div>
          ) : (
            <div className="product-showcase__carousel">
              <button
                type="button"
                className="product-showcase__arrow is-prev"
                aria-label="Previous products"
                onClick={() => scrollRow(-1)}
                disabled={!scrollState.canPrev}
              >
                <ChevronLeft size={22} />
              </button>
              <div className="product-showcase__row" ref={rowRef} onScroll={updateScrollState}>
                {activeProducts.map((product) => (
                  <ShowcaseCard key={product.id} product={product} kind={activeKey} now={now} />
                ))}
              </div>
              <button
                type="button"
                className="product-showcase__arrow is-next"
                aria-label="Next products"
                onClick={() => scrollRow(1)}
                disabled={!scrollState.canNext}
              >
                <ChevronRight size={22} />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
